import React, { useState, useEffect, useMemo } from "react";
import {
  StyleSheet,
  View,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ContactModel } from "common247";
import { useFirebase } from "@/components/firebase/useFirebase";
import { getMyContacts } from "@/services/firebase/user-functions";
import { ThemedText } from "@/components/ThemedText";
import { ThemedInput } from "@/components/ThemedInput";
import { ThemedButton } from "@/components/ThemedButton";
import { ThemedPopupScreen } from "@/components/ThemedPopupScreen";
import { useThemeColor } from "@/hooks/useThemeColor";
import { useComponentTheme } from "@/hooks/useComponentTheme";
import type { IPopoverAnchor } from "@/utils/popover-positioning";

/**
 * Props for the ContactSelector popup component.
 */
export interface IContactSelectorProps {
  /** Whether the contact selector popup is visible */
  visible: boolean;
  /** Callback fired when the selector is dismissed */
  onClose: () => void;
  /** Optional contextual coordinates of cursor or trigger element */
  anchorPosition?: IPopoverAnchor | null;
  /** Single contact selection callback */
  onSelectContact?: (contact: ContactModel) => void;
  /** Multi-contact selection callback */
  onSelectMultipleContacts?: (contacts: ContactModel[]) => void;
  /** Whether multi-select is enabled (default: false) */
  multiSelect?: boolean;
  /** Pre-selected contact IDs */
  selectedContactIds?: string[];
  /** Optional pre-fetched contacts */
  contacts?: ContactModel[];
  /** Optional title override (default: "Select Contact") */
  title?: string;
  /** Optional subtitle or instructions */
  subtitle?: string;
  /** Target width for the popover card (default: 440) */
  width?: number;
  /** Target maximum height for the popover card (default: 520) */
  maxHeight?: number;
  /** Optional testID for testing */
  testID?: string;
}

const DEFAULT_EMPTY_IDS: string[] = [];

/**
 * Contextual ContactSelector powered by ThemedPopupScreen.
 * Provides searchable contact selection anchored near the user cursor/trigger.
 */
export function ContactSelector({
  visible,
  onClose,
  anchorPosition,
  onSelectContact,
  onSelectMultipleContacts,
  multiSelect = false,
  selectedContactIds = DEFAULT_EMPTY_IDS,
  contacts: propContacts,
  title = "Select Contact",
  subtitle,
  width = 480,
  maxHeight = 520,
  testID = "contact-selector",
}: IContactSelectorProps) {
  const services = useFirebase();
  const [internalContacts, setInternalContacts] = useState<ContactModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>(selectedContactIds);

  const { selector: selectorTheme, palette } = useComponentTheme();
  const cardBg = palette.card;
  const borderColor = selectorTheme.row.restingBorderColor;
  const hoverBorderColor = selectorTheme.row.hoverBorderColor;
  const tintColor = selectorTheme.row.selectedBorderColor;
  const textMuted = selectorTheme.row.subtitleColor;
  const badgeBg = selectorTheme.avatarBadge.backgroundColor;

  useEffect(() => {
    setSelectedIds((prev) => {
      if (
        prev.length === selectedContactIds.length &&
        prev.every((id, idx) => id === selectedContactIds[idx])
      ) {
        return prev;
      }
      return selectedContactIds;
    });
  }, [selectedContactIds, visible]);

  useEffect(() => {
    if (!visible) return;
    if (propContacts && propContacts.length > 0) {
      setInternalContacts(propContacts);
      return;
    }
    let isMounted = true;
    const fetchContacts = async () => {
      setLoading(true);
      try {
        const res = await getMyContacts(services);
        if (isMounted && res.status && res.contacts) {
          setInternalContacts(res.contacts);
        }
      } catch (err) {
        console.error("[ContactSelector] Failed to load contacts:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchContacts();
    return () => {
      isMounted = false;
    };
  }, [visible, propContacts, services]);

  const filteredContacts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return internalContacts;
    return internalContacts.filter((c) => {
      const name = (c.displayName || c.fullName || "").toLowerCase();
      const email = (c.email || "").toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [internalContacts, searchQuery]);

  const handleToggleContact = (contact: ContactModel) => {
    const id = contact.id || contact.contactId;
    if (!id) return;

    if (!multiSelect) {
      onSelectContact?.(contact);
      onClose();
      return;
    }

    const nextSelected = selectedIds.includes(id)
      ? selectedIds.filter((item) => item !== id)
      : [...selectedIds, id];
    setSelectedIds(nextSelected);

    if (onSelectMultipleContacts) {
      const selectedModels = internalContacts.filter((c) =>
        nextSelected.includes(c.id || c.contactId || "")
      );
      onSelectMultipleContacts(selectedModels);
    }
  };

  const getInitials = (contact: ContactModel): string => {
    const name = contact.displayName || contact.fullName || "User";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  return (
    <ThemedPopupScreen
      visible={visible}
      onClose={onClose}
      anchorPosition={anchorPosition}
      title={title}
      subtitle={subtitle || (multiSelect ? `${selectedIds.length} selected` : undefined)}
      icon="people-outline"
      width={width}
      maxHeight={maxHeight}
      testID={testID}
      footer={
        <View style={styles.footerRow}>
          <ThemedButton
            title="Cancel"
            variant="secondary"
            onPress={onClose}
          />
          {multiSelect && (
            <ThemedButton
              title="Done"
              variant="primary"
              compact
              onPress={onClose}
              testID={`${testID}-done-btn`}
            />
          )}
        </View>
      }
    >
      <View style={styles.container}>
        {/* Search input */}
        <View style={styles.searchRow}>
          <ThemedInput
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholder="Search contacts..."
            style={styles.searchInput}
            testID={`${testID}-search-input`}
          />
        </View>

        {/* Content list */}
        {loading ? (
          <View style={styles.centerState}>
            <ActivityIndicator size="small" color={tintColor} />
            <ThemedText style={[styles.stateText, { color: textMuted }]}>
              Loading contacts...
            </ThemedText>
          </View>
        ) : filteredContacts.length === 0 ? (
          <View style={styles.centerState}>
            <Ionicons name="people-outline" size={32} color={textMuted} />
            <ThemedText style={[styles.stateText, { color: textMuted }]}>
              {searchQuery ? "No matching contacts" : "No contacts available"}
            </ThemedText>
          </View>
        ) : (
          <ScrollView
            style={styles.contactList}
            contentContainerStyle={styles.listContent}
            showsVerticalScrollIndicator={true}
            nestedScrollEnabled={true}
            keyboardShouldPersistTaps="handled"
          >
            {filteredContacts.map((contact) => {
              const id = contact.id || contact.contactId || "";
              const isSelected = selectedIds.includes(id);

              return (
                <TouchableOpacity
                  key={id}
                  style={[
                    styles.contactRow,
                    {
                      backgroundColor: isSelected
                        ? selectorTheme.row.selectedBg
                        : selectorTheme.row.restingBg,
                      borderColor: isSelected ? tintColor : borderColor,
                    },
                    isSelected && styles.selectedRow,
                  ]}
                  onPress={() => handleToggleContact(contact)}
                  activeOpacity={0.7}
                  testID={`${testID}-item-${id}`}
                >
                  <View style={[styles.avatarBadge, { backgroundColor: badgeBg }]}>
                    <ThemedText type="defaultSemiBold" style={[styles.avatarText, { color: selectorTheme.avatarBadge.textColor }]}>
                      {getInitials(contact)}
                    </ThemedText>
                  </View>

                  <View style={styles.infoCol}>
                    <ThemedText type="defaultSemiBold" style={[styles.contactName, { color: selectorTheme.row.titleColor }]} numberOfLines={1}>
                      {contact.displayName || contact.fullName || "Unnamed Contact"}
                    </ThemedText>
                    {contact.email ? (
                      <ThemedText style={[styles.contactEmail, { color: textMuted }]} numberOfLines={1}>
                        {contact.email}
                      </ThemedText>
                    ) : null}
                  </View>

                  <View style={styles.trailingCol}>
                    {isSelected ? (
                      <Ionicons name="checkmark-circle" size={20} color={tintColor} />
                    ) : (
                      <Ionicons name="chevron-forward" size={16} color={textMuted} />
                    )}
                  </View>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        )}
      </View>
    </ThemedPopupScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: 240,
  },
  searchRow: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  searchInput: {
    height: 32,
    fontSize: 12,
  },
  contactList: {
    flex: 1,
  },
  listContent: {
    paddingHorizontal: 8,
    paddingBottom: 6,
    gap: 4,
  },
  contactRow: {
    flexDirection: "row",
    alignItems: "center",
    padding: 6,
    borderRadius: 6,
    borderWidth: 1,
  },
  selectedRow: {
    borderWidth: 1.5,
  },
  avatarBadge: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 8,
  },
  avatarText: {
    fontSize: 12,
  },
  infoCol: {
    flex: 1,
    marginRight: 8,
  },
  contactName: {
    fontSize: 13,
  },
  contactEmail: {
    fontSize: 11,
    marginTop: 1,
  },
  trailingCol: {
    marginLeft: 6,
  },
  centerState: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 24,
    gap: 6,
  },
  stateText: {
    fontSize: 12,
  },
  footerRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
  },
});

